import { sv, VNode } from "@dhx/ts-common/dom";
import { IRadarScaleDrawData } from "../types";
import { verticalBottomText, verticalCenteredText, verticalTopText } from "./common";

export function getCoordinates(
	percent: number,
	radiusX: number,
	radiusY: number,
	stroke?: number
): [number, number] {
	if (stroke) {
		percent = percent + (2 * radiusX * Math.asin((0.5 * stroke) / radiusX)) / (2 * Math.PI * radiusX);
	}
	const x = Math.cos(2 * Math.PI * percent) * radiusX;
	const y = Math.sin(2 * Math.PI * percent) * radiusY;
	return [x, y];
}

export function shiftCoordinates(item: [number, number], dx: number, dy: number): [number, number] {
	return [item[0] + dx, item[1] + dy];
}

function setTransform(elem, shiftX, shiftY) {
	elem.setAttribute("transform", `translate(${shiftX}, ${shiftY}) scale(1.05)`);
	elem.classList.add("dhx_pie-transform-delay");
}

function removeTransform(elem) {
	elem.setAttribute("transform", `translate(0, 0)`);
	elem.classList.remove("dhx_pie-transform-delay");
}

export const pieLikeHandlers = {
	onmouseover(shiftX: number, shiftY: number, _, node: VNode) {
		const id = node.parent.attrs.id;
		setTransform(node.el, shiftX, shiftY);
		node.parent.body.forEach(nodeEl => {
			if (nodeEl.attrs.id === `${id}-text` || nodeEl.attrs.id === `${id}-connector`) {
				setTransform(nodeEl.el, shiftX, shiftY);
			}
		});
	},
	onmouseout(_, node: VNode) {
		const id = node.parent.attrs.id;
		removeTransform(node.el);
		node.parent.body.forEach(nodeEl => {
			if (nodeEl.attrs.id === `${id}-text` || nodeEl.attrs.id === `${id}-connector`) {
				removeTransform(nodeEl.el);
			}
		});
	},
};

function checkMiss(v: number, r: number): boolean {
	const miss = 0.000001;
	return v - miss < r && v + miss > r;
}

function drawBackgroundCircle(radius: number, color: string): VNode {
	return sv("circle", {
		cx: 0,
		cy: 0,
		r: radius,
		fill: color,
		stroke: "none",
		class: "background-circle",
	});
}

function arc(r: number, flag: boolean): string {
	return `M${-r},0A${r},${r} 0 ${flag ? 0 : 1} 1 ${r},0A${r},${r} 0 ${flag ? 0 : 1} 1 ${-r},0`;
}

export function radarScale(data: IRadarScaleDrawData, width: number, height: number): VNode {
	const getScaleAriaAttrs = (text: string) => ({
		"aria-label": `x-axis${text ? ", " + text : ""}`,
	});

	let radius;
	if (height > width) {
		radius = width / 2;
	} else {
		radius = height / 2;
	}
	const scalePercent = 1 / data.scales.length;
	const largeArcFlag = scalePercent > 0.5 ? 1 : 0;
	const svg = [];
	const background = drawBackgroundCircle(radius, "#FAFBFD");
	svg.push(background);
	let currentPercent = -0.25;
	const grid = [];
	const axis = data.axis;
	const gridClass = `radar-grid ${data.zebra ? "zebra" : ""}`;
	for (let i = 1; i < axis.length; i += 2) {
		const r1 = radius * axis[i - 1];
		const r2 = radius * axis[i];
		const d = `${arc(r1, true)} ${arc(r2, false)}`;
		const arcs = sv("path", {
			d,
			fill: "none",
			stroke: "black",
			class: gridClass,
		});
		grid.push(arcs);
	}
	svg.push(grid);
	data.scales.forEach(item => {
		const [startX, startY] = getCoordinates(currentPercent, radius, radius);
		const nextPercent = currentPercent + scalePercent;
		const [endX, endY] = getCoordinates(nextPercent, radius, radius);
		const d = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0`;
		const path = sv("path", {
			d,
			stroke: "black",
			fill: "none",
			class: "radar-scale",
		});
		svg.push(path);

		const [yTextPadding, xTextPadding] = [8, 8];
		const dy =
			checkMiss(currentPercent, 0) || checkMiss(currentPercent, 0.5)
				? 0
				: currentPercent < 0 || currentPercent > 0.5
				? -yTextPadding
				: yTextPadding;
		const dx =
			checkMiss(currentPercent, -0.25) || checkMiss(currentPercent, 0.25)
				? 0
				: currentPercent < -0.25 || currentPercent > 0.25
				? -xTextPadding
				: xTextPadding;

		if (checkMiss(currentPercent, -0.25) || checkMiss(currentPercent, 0.25)) {
			const alignFn = checkMiss(currentPercent, -0.25) ? verticalTopText : verticalBottomText;
			const text = sv("text", { x: startX + dx, y: startY + dy, class: "scale-text" }, [alignFn(item)]);
			svg.push(text);
		} else {
			const className =
				currentPercent >= -0.25 && currentPercent <= 0.25
					? "start-text scale-text"
					: "end-text scale-text";
			const text = sv("text", { x: startX + dx, y: startY + dy, class: className }, [
				verticalCenteredText(item),
			]);
			svg.push(text);
		}

		currentPercent = nextPercent;
	});

	currentPercent = -0.25;

	if (data.realAxis) {
		const scaleText = data.realAxis.map((item, index) => {
			const [x, y] = getCoordinates(-0.25, radius * axis[index], radius * axis[index]);
			return sv("text", { x, y, dx: -10, class: "radar-axis-text" }, [
				verticalCenteredText(item.toString()),
			]);
		});
		svg.push(scaleText);
	}
	return sv(
		"g",
		{
			transform: `translate(${width / 2}, ${height / 2})`,
			...getScaleAriaAttrs(data.attribute),
		},
		svg
	);
}
